import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { 
  CreditCard, 
  Check, 
  Crown,
  Zap,
  Shield,
  Loader2,
  CheckCircle
} from 'lucide-react';

const SUBSCRIPTION_PLANS = {
  basic: {
    name: 'Foundation',
    price: '$19.99',
    priceValue: 19.99,
    features: ['Workout Library', 'Basic Tracking', 'Community Access', 'Mobile App'],
    icon: Zap,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10'
  },
  premium: {
    name: 'Performance',
    price: '$39.99',
    priceValue: 39.99,
    features: ['All Foundation Features', 'AI Workout Builder', 'AI Meal Plans', '1-1 Messaging', 'Priority Support'],
    icon: Crown,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10',
    highlighted: true
  },
  elite: {
    name: 'Black Card',
    price: '$59.99',
    priceValue: 59.99,
    features: ['All Performance Features', 'Live Coaching', 'Custom Programs', 'HSA/FSA Eligible', 'Unlimited Everything'],
    icon: Shield,
    color: 'text-primary',
    bgColor: 'bg-primary/10'
  }
};

export default function SubscriptionPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    fetchSubscription();
    
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [token, sessionId]);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const response = await axios.get(`${API}/subscriptions/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.payment_status === 'paid') {
          toast.success('Subscription activated!');
          fetchSubscription();
          navigate('/subscription', { replace: true });
          return;
        } else if (response.data.status === 'expired') {
          toast.error('Payment session expired');
          navigate('/subscription', { replace: true });
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.info('Payment is processing. Please check back later.');
          navigate('/subscription', { replace: true });
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        navigate('/subscription', { replace: true });
      } finally {
        setCheckingPayment(false);
      }
    };
    
    poll();
  };

  const subscribe = async (plan) => {
    setSubscribing(plan);
    try {
      const response = await axios.post(
        `${API}/subscriptions/checkout?plan=${plan}&origin_url=${encodeURIComponent(window.location.origin)}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
      setSubscribing(null);
    }
  };

  if (loading || checkingPayment) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
        {checkingPayment && (
          <p className="text-gray-400">Verifying payment...</p>
        )}
      </div>
    );
  }

  return (
    <div data-testid="subscription-page" className="space-y-6">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
          CHOOSE YOUR <span className="text-primary">PLAN</span>
        </h1>
        <p className="text-gray-400 mt-2">
          Unlock your full potential with our premium features
        </p>
      </div>

      {/* Current Subscription */}
      {currentSubscription && (
        <div className="max-w-xl mx-auto">
          <div className="card-tactical border-primary bg-primary/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="text-white font-semibold capitalize">
                    {currentSubscription.plan} Plan Active
                  </div>
                  <div className="text-sm text-gray-400">
                    ${currentSubscription.price}/month
                  </div>
                </div>
              </div>
              <div className="px-3 py-1 bg-green-500/20 text-green-500 text-sm rounded-sm uppercase font-semibold">
                Active
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {Object.entries(SUBSCRIPTION_PLANS).map(([key, plan]) => {
          const Icon = plan.icon;
          const isCurrentPlan = currentSubscription?.plan === key;
          
          return (
            <div 
              key={key}
              className={`card-tactical relative flex flex-col ${
                plan.highlighted ? 'border-primary' : ''
              } ${isCurrentPlan ? 'ring-2 ring-primary' : ''}`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-white text-xs uppercase font-bold px-3 py-1 rounded-sm">
                    Most Popular
                  </span>
                </div>
              )}
              
              <div className={`w-12 h-12 ${plan.bgColor} rounded-sm flex items-center justify-center mb-4`}>
                <Icon className={`w-6 h-6 ${plan.color}`} />
              </div>
              
              <h3 className="font-heading text-2xl text-white uppercase">{plan.name}</h3>
              
              <div className="flex items-baseline gap-1 mt-2 mb-6">
                <span className="font-mono text-4xl font-bold text-white">{plan.price}</span>
                <span className="text-gray-400">/month</span>
              </div>
              
              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-gray-300">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              
              <Button
                data-testid={`subscribe-${key}-btn`}
                onClick={() => subscribe(key)}
                disabled={subscribing === key || isCurrentPlan}
                className={`w-full uppercase font-semibold ${
                  isCurrentPlan 
                    ? 'bg-green-600 hover:bg-green-600 cursor-not-allowed' 
                    : plan.highlighted 
                      ? 'bg-primary hover:bg-red-700 text-white' 
                      : 'bg-secondary hover:bg-primary text-white'
                }`}
              >
                {subscribing === key ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isCurrentPlan ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Current Plan
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Subscribe
                  </>
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {/* HSA/FSA Notice */}
      <div className="max-w-2xl mx-auto text-center">
        <div className="p-4 bg-secondary/50 rounded-sm border border-border">
          <div className="flex items-center justify-center gap-2 text-green-500 mb-2">
            <Shield className="w-5 h-5" />
            <span className="font-semibold">HSA/FSA Eligible</span>
          </div>
          <p className="text-sm text-gray-400">
            Our Elite plan qualifies for HSA/FSA reimbursement. Contact your plan administrator for details.
          </p>
        </div>
      </div>
    </div>
  );
}
